import { generateBlockId } from "./layoutSlotUtils";

/** Grille d’aperçu (sans `bg` — uniquement positions). */
export const LAYOUT_BUILDER_DESIGN_DEFS = {
  "4col": {
    colCount: 4,
    gridTemplateRows: "67fr 33fr",
    slotDefs: [
      { id: "slot-1", colStart: 1, colEnd: 2, rowStart: 1, rowEnd: 2 },
      { id: "slot-2", colStart: 1, colEnd: 2, rowStart: 2, rowEnd: 3 },
      { id: "slot-3", colStart: 2, colEnd: 3, rowStart: 1, rowEnd: 3 },
      { id: "slot-4", colStart: 3, colEnd: 4, rowStart: 1, rowEnd: 3 },
      { id: "slot-5", colStart: 4, colEnd: 5, rowStart: 1, rowEnd: 2 },
      { id: "slot-6", colStart: 4, colEnd: 5, rowStart: 2, rowEnd: 3 },
    ],
  },
};

export const STATIC_BLOCKS = [
  { blockType: "COURSES_WEEKLY", label: "Shiurim" },
  { blockType: "ZMANIM", label: "Zmanim" },
  { blockType: "ANNOUNCEMENTS_ADS", label: "Announcements" },
  { blockType: "QR_CODE", label: "QR code" },
  { blockType: "NEXT_RDV", label: "Next appointment" },
];

export function getBlockLabel(item, t) {
  if (!item) return t ? t("layoutBuilder.blocks.unknown") : "Unknown block";
  if (item.blockType) {
    if (t) {
      const keys = {
        COURSES_WEEKLY: "shiurim",
        ZMANIM: "zmanim",
        ANNOUNCEMENTS_ADS: "announcements",
        QR_CODE: "qrCode",
        NEXT_RDV: "nextRdv",
      };
      if (keys[item.blockType]) return t(`layoutBuilder.blocks.${keys[item.blockType]}`);
    } else {
      const staticMatch = STATIC_BLOCKS.find((s) => s.blockType === item.blockType);
      if (staticMatch) return staticMatch.label;
    }

    const name = item.sectionName || item.sectionId || "";
    if (item.blockType === "WEEK_SECTION") {
      const label = t ? t("layoutBuilder.blocks.week") : "Week";
      return name ? `${label} — ${name}` : label;
    }
    if (item.blockType === "SHABBAT_SECTION") {
      const label = t ? t("layoutBuilder.blocks.shabbat") : "Shabbat";
      return name ? `${label} — ${name}` : label;
    }
    if (item.blockType === "CHAGUIM_SECTION") {
      const label = t ? t("layoutBuilder.blocks.chaguim") : "Chaguim";
      if (!name) return label;
      return item.category ? `${item.category} — ${name}` : `${label} — ${name}`;
    }
  }
  return t ? t("layoutBuilder.blocks.unknown") : "Unknown block";
}

/** Nom affichable de la section (bloc section-based), pour les zones de drop. */
export function sectionLabelForBlock(assignment, sectionsPalette = []) {
  if (!assignment?.sectionId) return null;
  if (assignment.sectionName) return assignment.sectionName;
  const m = sectionsPalette.find((s) => String(s.sectionId) === String(assignment.sectionId));
  return m?.sectionName || String(assignment.sectionId);
}

export function getDropZoneBlockTitle(assignment, t) {
  if (!assignment?.blockType) return t ? t("layoutBuilder.blocks.unknown") : "Unknown";
  if (t) {
    const keys = {
      COURSES_WEEKLY: "shiurim",
      ZMANIM: "zmanim",
      ANNOUNCEMENTS_ADS: "announcements",
      QR_CODE: "qrCode",
      NEXT_RDV: "nextRdv",
    };
    if (keys[assignment.blockType]) return t(`layoutBuilder.blocks.${keys[assignment.blockType]}`);
    if (assignment.blockType === "WEEK_SECTION") return t("layoutBuilder.blocks.week");
    if (assignment.blockType === "SHABBAT_SECTION") return t("layoutBuilder.blocks.shabbat");
    if (assignment.blockType === "CHAGUIM_SECTION") return assignment.category || t("layoutBuilder.blocks.chaguim");
  } else {
    const staticMatch = STATIC_BLOCKS.find((s) => s.blockType === assignment.blockType);
    if (staticMatch) return staticMatch.label;
    if (assignment.blockType === "WEEK_SECTION") return "Week";
    if (assignment.blockType === "SHABBAT_SECTION") return "Shabbat";
    if (assignment.blockType === "CHAGUIM_SECTION") return assignment.category || "Chaguim";
  }
  return getBlockLabel(assignment, t);
}

export function makeDefaultAssignment(paletteItem) {
  const base = {
    id: generateBlockId(),
    blockType: paletteItem.blockType,
    autoScroll:
      paletteItem.blockType === "ZMANIM" || paletteItem.blockType === "NEXT_RDV" ? true : false,
  };

  if (paletteItem.sectionId) {
    base.sectionId = paletteItem.sectionId;
    if (paletteItem.sectionName != null) base.sectionName = paletteItem.sectionName;
    if (paletteItem.category != null) base.category = paletteItem.category;
  }
  return base;
}

export function designKeyToThumbSuffix(designKey) {
  if (designKey === "4col") return "4Col";
  if (designKey === "3col") return "3Col";
  return designKey;
}

export function templateThumbUrls(templateName, designKey) {
  const part = designKeyToThumbSuffix(designKey);
  const base = `${templateName}_${part}`;
  return {
    png: `/images/templates/${base}.png`,
    jpg: `/images/templates/${base}.jpg`,
  };
}

/** `67fr 33fr` → `minmax(0, 67fr) …` pour grille d’aperçu figée. */
export function gridRowsToMinmax(gridTemplateRows) {
  const raw = gridTemplateRows || "67fr 33fr";
  return raw
    .trim()
    .split(/\s+/)
    .map((token) => (token.includes("fr") ? `minmax(0, ${token})` : token))
    .join(" ");
}

/**
 * @param {string} draggableId - `static:ZMANIM` ou `section:WEEK_SECTION:uuid`
 * @param {Array} paletteItems
 * @returns {object|null}
 */
export function paletteItemFromDragId(draggableId, paletteItems) {
  const [kind, ...rest] = draggableId.split(":");
  if (kind === "static") {
    const blockType = rest[0];
    const item = { blockType };
    if (blockType === "ZMANIM" || blockType === "NEXT_RDV") item.autoScroll = true;
    return item;
  }
  if (kind === "section") {
    const [blockType, sectionId] = rest;
    return (
      paletteItems.find((p) => p.blockType === blockType && String(p.sectionId) === String(sectionId)) || {
        blockType,
        sectionId,
      }
    );
  }
  return null;
}

export function defaultLayoutConfig() {
  return {
    screenNumber: 1,
    templateName: "Armon",
    slides: [
      { id: "slide-1", design: "4col", delaySec: 5, slots: {} },
    ],
  };
}
